import { twMerge } from 'tailwind-merge';

import { GoogleMapsLink } from './components/GoogleMapsLink';
import { ExternalLink } from '../../../../components/Clickables/ExternalLink/ExternalLink';
import { InternalLink } from '../../../../components/Clickables/InternalLink/InternalLink';
import { Pill } from '../../../../components/Clickables/Pill/Pill';
import { Heading } from '../../../../components/Heading/Heading';
import { Text } from '../../../../components/Text/Text';
import { LatLng } from '../../../../components/LatLng/LatLng';
import { getEdgesFromConnection } from '../../../../helpers/graphql';
import { getSpeciesUrl } from '../../../../helpers/urls';
import {
  GetFishingWater_fishingWater_reviews_amenities_edges_node,
  GetFishingWater_fishingWater_country,
  WaterTypes,
  GetFishingWater_fishingWater_region,
  GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary,
  GetFishingWater_fishingWater_topAnglers_anglers,
  GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges_node,
  GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges,
  GetFishingWater_fishingWater_metadata,
  GetFishingWater_fishingWater_county,
  GetFishingWater_fishingWater_intersectingCounties_edges_node,
} from '../../../../interfaces/graphql';
import { SUB_LINK_IDS } from '../../helpers/fishingWaterHelpers';
import ReactAnd from 'react-and';
import { addIndefiniteArticleToWaterType } from '../../../../helpers/FishingWater/addIndefiniteArticleToWaterType';
import { FishingWaterMetadataPills } from '../../../../components/FishingWaterMetadataPills/FishingWaterMetadataPills';
import { LocationLinks } from '../../../../components/LocationLinks/LocationLinks';
import { OtherAnglers } from '../../../../components/OtherAnglers/OtherAnglers';
import { IntersectingCountiesLinks } from '../../../../components/IntersectingCountiesLinks/IntersectingCountiesLinks';

interface IProps {
  amenitiesList: GetFishingWater_fishingWater_reviews_amenities_edges_node[];
  county: GetFishingWater_fishingWater_county | null;
  country: GetFishingWater_fishingWater_country | null;
  descriptionOfWaterType: WaterTypes;
  fishingWaterId: string;
  fishingWaterName: string;
  intersectingCounties: GetFishingWater_fishingWater_intersectingCounties_edges_node[];
  latitude: number;
  longitude: number;
  metadata: GetFishingWater_fishingWater_metadata[];
  region: GetFishingWater_fishingWater_region | null;
  url: string | null;
  topSpeciesSummary?: GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary;
  className?: string;
  topAnglers: GetFishingWater_fishingWater_topAnglers_anglers | null;
}

export const GeneralInfoSection = ({
  amenitiesList,
  county,
  country,
  descriptionOfWaterType,
  fishingWaterId,
  fishingWaterName,
  intersectingCounties,
  latitude,
  longitude,
  metadata,
  region,
  topSpeciesSummary,
  url,
  className,
  topAnglers,
}: IProps) => {
  const countyName = county?.name ?? undefined;
  const countryName = country?.countryName;
  const countryId = country?.id;

  const speciesEdges = getEdgesFromConnection<
    GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges_node,
    GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges
  >(topSpeciesSummary);

  return (
    <div
      id={SUB_LINK_IDS.GENERAL_INFO}
      className={twMerge(
        'w-full scroll-mt-[--navigation-scrolltop-offset-mobile] sm:scroll-mt-[--navigation-scrolltop-offset]',
        className,
      )}
    >
      <div className="flex w-full flex-col gap-8 bg-lake-fog px-8 py-10 sm:rounded-xl sm:px-10 md:px-12">
        <div className="flex flex-col gap-2">
          <Heading variant="sm" as="h3">
            General info
          </Heading>
          <Text variant="md" className="m-0">
            {countryId && countryName && (
              <span>
                {`${fishingWaterName} is ${addIndefiniteArticleToWaterType(descriptionOfWaterType)} located in `}
                <LocationLinks
                  regionId={region?.id}
                  regionName={region?.name}
                  countryId={countryId}
                  countryName={countryName}
                  countyName={countyName}
                  className="m-0 text-left text-primary"
                  LinkClassName="text-primary"
                  textAs="span"
                  textVariant="md"
                />
                {'. '}
              </span>
            )}

            <IntersectingCountiesLinks
              countyName={countyName}
              regionId={region?.id}
              regionName={region?.name}
              intersectingCounties={intersectingCounties}
            />

            {speciesEdges.length > 0 && (
              <span>
                {`It is most popular for fishing `}
                <ReactAnd>
                  {speciesEdges.map(({ node }) => {
                    return (
                      <InternalLink
                        key={`species-${node._id}`}
                        to={getSpeciesUrl(node._id, node.displayName)}
                        className="text-primary"
                      >
                        {node.displayName}
                      </InternalLink>
                    );
                  })}
                </ReactAnd>
                {`.`}
              </span>
            )}
          </Text>
        </div>
        {topAnglers && topAnglers.totalCount > 0 && (
          <OtherAnglers appDownloadSourceId={fishingWaterId} topAnglers={topAnglers} />
        )}
        <div className="flex flex-col gap-2">
          <Heading variant="xs" className="mb-0">
            Location
          </Heading>
          <div className="flex flex-row items-center gap-2">
            <LatLng textVariant="sm" latitude={latitude} longitude={longitude} />
            <GoogleMapsLink latitude={latitude} longitude={longitude} />
          </div>
        </div>
        {metadata.length > 0 && <FishingWaterMetadataPills metadata={metadata} />}
        {url && (
          <div className="flex flex-col gap-2">
            <Heading variant="xs" className="mb-0">
              Official website
            </Heading>
            <ExternalLink
              href={url}
              variant="textPrimary"
              target="blank"
              className="truncate text-abyss"
            >
              {url}
            </ExternalLink>
          </div>
        )}
        <div>
          {amenitiesList.length > 0 && (
            <div className="flex flex-col gap-2">
              <Heading variant="xs" className="mb-4">
                Amenities
              </Heading>
              <div className="flex flex-row flex-wrap gap-2">
                {amenitiesList.map(amenity => (
                  <Pill
                    key={`amenity-item-${amenity.summaryHeader}`}
                    variant="infoBlue"
                    disabled
                    className="rounded-[24px] px-4 py-2 text-[13px]"
                  >
                    {amenity.summaryHeader}
                  </Pill>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
